/**
 * The billing rows the BO reads back, named after the backend classes they mirror —
 * `serverless/apigateway/billing/src/models/billing.ts` in `fleetback-data-rest-api`.
 *
 * These projections stay local on purpose: `@arhs-fleetback/types/entities/billing` publishes the
 * enums and the query/request types, **not** the response shapes, which are internal to the
 * billing service. Keep the names identical so the correspondence stays greppable across repos.
 */
import type { BillingPeriodEnum, BillingTypeEnum } from 'modules/metrics/constants';

/**
 * The provider a channel is served by. Mirror of the backend's `BillingServiceTypeEnum`
 * (`serverless/model/src/billing/resources/billing.service.type.enum.ts`).
 *
 * TODO: import from `@arhs-fleetback/types/entities/billing` once published — that subpath is
 * added by `fleetback-data-rest-api!6588` and does ship the enums; 0.0.78 has it not yet.
 */
export const BILLING_SERVICE_TYPE = {
  CMCOM: 'CMCOM',
  FLOWLYNE: 'FLOWLYNE',
  PXL_VISION: 'PXL_VISION',
  REMOVEBG: 'REMOVEBG',
  TWILIO: 'TWILIO',
  TYNTEC: 'TYNTEC',
} as const;

export type BillingServiceTypeEnum = (typeof BILLING_SERVICE_TYPE)[keyof typeof BILLING_SERVICE_TYPE];

/**
 * Which template the XLSX export is built from — an admin export carries the provider column and
 * the filters, a manager export neither. Mirror of the backend's `ExportBillingTypeEnum`.
 *
 * TODO: same package move as {@link BILLING_SERVICE_TYPE}.
 */
export const EXPORT_BILLING_TYPE = {
  ADMIN: 'ADMIN',
  MANAGER: 'MANAGER',
} as const;

export type ExportBillingTypeEnum = (typeof EXPORT_BILLING_TYPE)[keyof typeof EXPORT_BILLING_TYPE];

/** The message contract properties the SMS channel reads to compute its overcost. */
export type BillingContractProperties = {
  price?: number;
  quota: number;
};

/**
 * What every aggregation row carries, dealer or group.
 *
 * No backend counterpart: `WrappedDealerBilling` and `WrappedGroupBilling` are two independent
 * classes there, this only factors the fields they share.
 *
 * The per-provider money fields are optional on every row: the API sets only the ones its channel
 * maps to (`removebg` for CLIPPING, `flowlyne` for PHONE_AGENT, …). Same for `duration`, which only
 * PHONE_AGENT populates, and for `count`, which is a call count there and an item count elsewhere.
 */
type AbstractWrappedBilling = {
  count: number;
  /** Total call duration in seconds — PHONE_AGENT only. */
  duration?: number;
  currency?: string;
  period?: string;
  periodType: BillingPeriodEnum;
  type: BillingTypeEnum;
  cmcom?: number;
  flowlyne?: number;
  pxlvision?: number;
  removebg?: number;
  twilio?: number;
  tyntec?: number;
};

/** A row of `/billing/api/{channel}/dealers`. */
export type WrappedDealerBilling = AbstractWrappedBilling & {
  dealerId: string;
  dealerName: string;
  administrator?: string;
  contract?: BillingContractProperties;
  chargedOvercost?: number;
  realOvercost?: number;
  provider?: BillingServiceTypeEnum;
};

/** A row of `/billing/api/{channel}/groups` — the groups response carries no `provider`. */
export type WrappedGroupBilling = AbstractWrappedBilling & {
  groupId: string;
  groupName: string;
};

/** What a slice's `data` holds: whichever of the two views was last fetched. Backend-side union. */
export type WrappedBilling = WrappedDealerBilling | WrappedGroupBilling;
